import { isElectronApp } from '#~/electron/utils.js';
import {
  shouldSettleDeleteUi,
  type ControllerStopResult,
  type IPControllerInfo
} from '#~/ipjoystick/comm.js';
import { useProcesses } from './hooks.js';
import {
  useRemoveDevice,
  useAddDevice,
  useJoystickConnection,
  useRenameDevice,
  useRelaunchDevice
} from './hooks.js';
import { AdvancedSettingsModal, ControllerStopNotice } from './settings.js';
import { Button } from '@studio/design-system/button';
import { Callout } from '@studio/design-system/callout';
import { useDialog } from '@studio/design-system/dialog';
import { usePrompt } from '@studio/design-system/prompt';
import { useState } from 'react';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import { ItemsList } from '../components/index.js';
import { ModalUI } from './ui.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { DataSTag, Sel, STagNew } from '#~/stag/index.js';
import { logger } from '#~/logger/index.js';

export const ControllersList = () => {
  const { processes: controllers, reload } = useProcesses();
  const { track } = useAnalytics();

  const list = controllers.map((controller) => ({
    id: controller.port.toString(),
    left: controller.name,
    ...(controller.status === 'error' && { right: 'Not running' })
  }));
  // Bridges that exited on their own; each gets a relaunch action.
  const crashed = controllers.filter((controller) => controller.status === 'error');

  const [selected, setSelected] = useState<(typeof list)[number]>();
  const [stopNotice, setStopNotice] = useState<ControllerStopResult | null>(null);
  const settings = useDialog(AdvancedSettingsModal);
  const removeDevice = useRemoveDevice();
  const renameDevice = useRenameDevice();
  const relaunchDevice = useRelaunchDevice();

  const onRemoveDevice = async (port: number) => {
    const result = await removeDevice(port);
    setStopNotice(result);
    // Refresh on the settled fact, not the delete event: a retry after a
    // first attempt already removed the row still has to drop the stale row.
    if (result.savedAbsent) reload();
    return result;
  };

  const prompt = usePrompt();

  const { netInfo, currentPort, addDevice, updateNetInfo } = useAddDevice();

  const networks =
    netInfo?.networks.map((n) => ({
      ...n,
      port: netInfo.port,
      type: 'default'
    })) ?? [];

  const onSearch = () => {
    updateNetInfo();
  };

  const [showAddModal, setShowAddModal] = useState(false);

  const { isConnected, setConnected } = useJoystickConnection(currentPort);

  const onAddDevice = async (ip: string, port: number) => {
    await addDevice({ ip, port });
    reload();
    track(EVENT_NAMES.SUCCESSFULLY_ADD_IP_CONTROLLER, EVENT_CATEGORIES.CONTROLLERS, {
      controllerType: 'ip',
      ipAddress: ip,
      port
    });
  };

  const onClose = () => {
    setShowAddModal(false);
    setConnected(false);
  };

  const onRenameDevice = async (controller: IPControllerInfo, name: string) => {
    await renameDevice(controller, name);
    reload();
  };

  const onRelaunchDevice = async (controller: IPControllerInfo) => {
    try {
      await relaunchDevice(controller);
    } catch (error) {
      logger.warning('IP controller relaunch failed', error);
    } finally {
      reload();
    }
  };

  const isElectron = isElectronApp();

  return (
    <div className='mt-16 max-w-[350px]'>
      {!isElectron && (
        <Callout
          className='mb-16'
          title={`IP controllers cannot be managed on web. Please use the ${vendorName()} application to add and manage your controllers.`}
        />
      )}

      <ControllerStopNotice result={stopNotice} />
      {isElectron &&
        crashed.map((controller) => (
          <Callout
            key={controller.port}
            className='mb-16'
            severity='error'
            title={`${controller.name} stopped unexpectedly.`}
          >
            <Button
              size='sm'
              // Per rule: keyboard-reachable-interactive-elements (ai.knowledge/global) — Button has no focus ring.
              className='focus-visible:ring-primary/50 mt-8 focus-visible:ring-2'
              onClick={() => void onRelaunchDevice(controller)}
            >
              Relaunch
            </Button>
          </Callout>
        ))}

      <ItemsList
        copy={{
          leftTitle: 'Saved IP controllers',
          noItemsCopy: 'You currently have no saved controllers.'
        }}
        list={list}
        selected={selected}
        setSelected={setSelected}
        tagIpControllers
        {...(isElectron && {
          onSettings: (item) => {
            const controller = controllers.find((c) => c.port.toString() === item.id);
            if (!controller) return;

            settings.open({
              name: controller.name,
              ip: controller.localIP,
              port: controller.port,
              onDelete: (showResult) => {
                prompt.confirm({
                  ...DataSTag(Sel.AccountSettings, 'ip_controller_delete_prompt'),
                  message: 'Are you sure you want to delete this controller?',
                  title: 'Delete Controller',
                  cancel: {
                    ...STagNew(Sel.AccountSettings, 'ip_controller_delete_cancel'),
                    label: 'Cancel'
                  },
                  okay: {
                    ...STagNew(Sel.AccountSettings, 'ip_controller_delete_confirm'),
                    label: 'Delete Controller',
                    variant: 'danger'
                  },
                  onConfirm(answer) {
                    if (answer) {
                      void onRemoveDevice(controller.port).then((result) => {
                        showResult(result);
                        if (shouldSettleDeleteUi(result)) settings.close();
                      });
                    }
                  }
                });
              },
              onSave: async (name) => {
                if (!name.trim()) return;
                await onRenameDevice(controller, name.trim());
                settings.close();
              }
            });
          }
        })}
      />

      {isElectron && (
        <>
          <Button
            className='mt-16 w-full'
            onClick={() => {
              updateNetInfo();
              setShowAddModal(true);
              track(EVENT_NAMES.PRESS_ADD_IP_CONTROLLER, EVENT_CATEGORIES.CONTROLLERS);
            }}
            {...STagNew(Sel.AccountSettings, 'ip_controller_add')}
          >
            Add IP Controller
          </Button>
          {showAddModal && (
            <ModalUI
              isConnected={isConnected}
              networks={networks}
              onAddDevice={onAddDevice}
              onRemoveDevice={onRemoveDevice}
              onClose={onClose}
              onSearch={onSearch}
            />
          )}
        </>
      )}
    </div>
  );
};
